<template>
  <div class="pa-4">
    <!-- Header -->
    <div class="d-flex align-center mb-6">
      <v-btn icon="mdi-arrow-left" variant="text" @click="$router.back()" class="mr-2"></v-btn>
      <div>
        <h1 class="text-h5 font-weight-black">Ads</h1>
        <p class="text-caption text-grey mt-1">Banner ads shown in the right sidebar</p>
      </div>
      <v-spacer></v-spacer>
      <v-btn color="primary" variant="flat" prepend-icon="mdi-plus" class="rounded-pill font-weight-bold" @click="openCreate">
        New Ad
      </v-btn>
    </div>

    <!-- Access denied -->
    <v-alert v-if="!isAdminUser" type="error" variant="tonal" class="rounded-xl border mb-4">
      Access denied. This page is only accessible to the admin account.
    </v-alert>

    <template v-else>
      <div v-if="loading" class="text-center py-12">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <p class="mt-4 text-grey">Loading ads...</p>
      </div>

      <v-alert v-else-if="error" type="error" variant="tonal" class="rounded-xl border mb-4">{{ error }}</v-alert>

      <div
        v-else-if="items.length === 0"
        class="text-center py-12 border rounded-xl border-dashed"
        style="border-color: rgba(255,255,255,0.1) !important;"
      >
        <v-icon icon="mdi-image-outline" size="64" color="grey-darken-2" class="mb-4"></v-icon>
        <h3 class="text-h6 text-grey">No ads yet</h3>
        <p class="text-body-2 text-grey-darken-1 mt-2">Create one to show it in the sidebar.</p>
      </div>

      <div v-else class="d-flex flex-wrap ga-4">
        <v-card
          v-for="item in items"
          :key="item.rkey"
          class="rounded-xl bg-surface border"
          style="border-color: rgba(255,255,255,0.08) !important; width: 280px;"
          :style="!item.active ? 'opacity: 0.45;' : ''"
        >
          <v-img
            :src="resolvedImages[item.rkey!] || ''"
            height="160"
            cover
            class="rounded-t-xl bg-surface-variant"
          >
            <template #placeholder>
              <div class="d-flex align-center justify-center h-100">
                <v-icon icon="mdi-image" size="48" color="grey-darken-2"></v-icon>
              </div>
            </template>
          </v-img>

          <v-card-text class="pa-3">
            <div class="text-caption text-grey text-truncate mb-2">{{ item.link }}</div>
            <div class="d-flex align-center ga-2">
              <v-chip
                :color="item.active ? 'success' : 'grey'"
                size="x-small"
                variant="tonal"
                :prepend-icon="item.active ? 'mdi-eye' : 'mdi-eye-off'"
                class="cursor-pointer"
                @click="toggleActive(item)"
              >
                {{ item.active ? 'Active' : 'Inactive' }}
              </v-chip>
              <v-spacer></v-spacer>
              <v-btn icon="mdi-pencil" size="x-small" variant="text" color="grey" @click="openEdit(item)"></v-btn>
              <v-btn icon="mdi-delete" size="x-small" variant="text" color="error" @click="confirmDelete(item)"></v-btn>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </template>

    <!-- Create / Edit Dialog -->
    <v-dialog v-model="showForm" max-width="500px" scrollable>
      <v-card class="rounded-xl bg-surface border" style="border-color: rgba(255,255,255,0.08) !important;">
        <v-card-title class="font-weight-black pa-5 pb-0 d-flex align-center justify-space-between">
          {{ editingItem ? 'Edit Ad' : 'New Ad' }}
          <v-btn icon="mdi-close" variant="text" size="small" @click="showForm = false"></v-btn>
        </v-card-title>

        <v-card-text class="pa-5">
          <v-form ref="formRef">
            <!-- Image upload -->
            <div class="mb-4">
              <div
                class="image-upload-area rounded-xl d-flex align-center justify-center cursor-pointer border"
                style="height: 180px; border-color: rgba(255,255,255,0.12) !important; border-style: dashed !important;"
                :style="previewUrl ? `background-image: url('${previewUrl}'); background-size: cover; background-position: center;` : ''"
                @click="triggerImageInput"
              >
                <div v-if="!previewUrl" class="text-center">
                  <v-icon icon="mdi-image-plus" size="40" color="grey-darken-1" class="mb-2"></v-icon>
                  <div class="text-caption text-grey">Click to upload image</div>
                </div>
                <v-btn
                  v-if="previewUrl"
                  icon="mdi-close"
                  size="x-small"
                  variant="tonal"
                  color="error"
                  class="ma-2"
                  style="position: absolute; top: 0; right: 0;"
                  @click.stop="clearImage"
                ></v-btn>
              </div>
              <input ref="imageInputRef" type="file" accept="image/*" class="d-none" @change="onImageFileChange" />
            </div>

            <v-text-field
              v-model="form.link"
              label="Link URL *"
              variant="outlined"
              density="comfortable"
              class="mb-3 rounded-lg"
              placeholder="https://example.com"
              :rules="[v => !!v || 'Link is required']"
            ></v-text-field>

            <v-switch
              v-model="form.active"
              label="Active (visible in sidebar)"
              color="primary"
              density="comfortable"
              hide-details
            ></v-switch>
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-5 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" class="font-weight-bold" @click="showForm = false">Cancel</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            class="rounded-pill font-weight-bold px-6"
            :loading="saving"
            @click="submitForm"
          >
            {{ editingItem ? 'Save Changes' : 'Publish' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirmation -->
    <v-dialog v-model="showDeleteConfirm" max-width="380px">
      <v-card class="rounded-xl bg-surface border" style="border-color: rgba(255,255,255,0.08) !important;">
        <v-card-title class="text-h6 font-weight-black pa-6">Delete Ad?</v-card-title>
        <v-card-text class="pa-6 pt-0 text-grey-lighten-1">This ad will be permanently removed.</v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" class="font-weight-bold" @click="showDeleteConfirm = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" class="rounded-pill font-weight-bold px-6" :loading="deleting" @click="doDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" rounded="pill" location="bottom">
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import {
  isAdmin,
  getAds,
  createAd,
  updateAd,
  deleteAd,
  resolveAdImageUrl,
  getAdminDid,
  uploadBlob,
  type Ad
} from '@/services/bluesky';

const isAdminUser = computed(() => isAdmin());

const items = ref<Ad[]>([]);
const resolvedImages = ref<Record<string, string>>({});
const loading = ref(true);
const error = ref('');

const showForm = ref(false);
const editingItem = ref<Ad | null>(null);
const saving = ref(false);
const formRef = ref<any>(null);

const showDeleteConfirm = ref(false);
const itemToDelete = ref<Ad | null>(null);
const deleting = ref(false);

const snackbar = ref({ show: false, text: '', color: 'success' });

// Image upload
const imageInputRef = ref<HTMLInputElement | null>(null);
const previewUrl = ref('');
const imageFile = ref<File | null>(null);
const existingImageBlob = ref<any>(null);

const form = ref({ link: '', active: true });

const notify = (text: string, color = 'success') => {
  snackbar.value = { show: true, text, color };
};

const load = async () => {
  loading.value = true;
  error.value = '';
  try {
    items.value = await getAds();
    const adminDid = await getAdminDid();
    for (const item of items.value) {
      if (item.rkey) {
        resolvedImages.value[item.rkey] = resolveAdImageUrl(item, adminDid);
      }
    }
  } catch (e: any) {
    error.value = e?.message || 'Failed to load';
  } finally {
    loading.value = false;
  }
};

const triggerImageInput = () => imageInputRef.value?.click();

const onImageFileChange = (e: Event) => {
  const file = (e.target as HTMLInputElement).files?.[0];
  if (!file) return;
  imageFile.value = file;
  previewUrl.value = URL.createObjectURL(file);
};

const clearImage = () => {
  imageFile.value = null;
  previewUrl.value = '';
  existingImageBlob.value = null;
  if (imageInputRef.value) imageInputRef.value.value = '';
};

const openCreate = () => {
  editingItem.value = null;
  form.value = { link: '', active: true };
  clearImage();
  showForm.value = true;
};

const openEdit = async (item: Ad) => {
  editingItem.value = item;
  form.value = { link: item.link, active: item.active };
  existingImageBlob.value = item.imageBlob || null;
  imageFile.value = null;
  if (item.rkey && resolvedImages.value[item.rkey]) {
    previewUrl.value = resolvedImages.value[item.rkey];
  } else {
    previewUrl.value = '';
  }
  showForm.value = true;
};

const submitForm = async () => {
  const { valid } = await formRef.value?.validate();
  if (!valid) return;
  saving.value = true;
  try {
    let imageBlob = existingImageBlob.value;
    if (imageFile.value) {
      imageBlob = await uploadBlob(imageFile.value, imageFile.value.type);
    }
    const payload = { link: form.value.link, active: form.value.active, imageBlob };
    if (editingItem.value?.rkey) {
      await updateAd(editingItem.value.rkey, payload);
      notify('Ad updated');
    } else {
      await createAd(payload);
      notify('Ad published');
    }
    showForm.value = false;
    await load();
  } catch (e: any) {
    notify(e?.message || 'Failed to save', 'error');
  } finally {
    saving.value = false;
  }
};

const toggleActive = async (item: Ad) => {
  try {
    await updateAd(item.rkey!, { link: item.link, imageBlob: item.imageBlob, active: !item.active });
    await load();
  } catch (e: any) {
    notify(e?.message || 'Failed to update', 'error');
  }
};

const confirmDelete = (item: Ad) => {
  itemToDelete.value = item;
  showDeleteConfirm.value = true;
};

const doDelete = async () => {
  if (!itemToDelete.value?.rkey) return;
  deleting.value = true;
  try {
    await deleteAd(itemToDelete.value.rkey);
    notify('Ad deleted');
    showDeleteConfirm.value = false;
    await load();
  } catch (e: any) {
    notify(e?.message || 'Failed to delete', 'error');
  } finally {
    deleting.value = false;
  }
};

onMounted(() => {
  if (isAdminUser.value) load();
  else loading.value = false;
});
</script>
